<template>
    <div>
        <div class="text-center mb-4">
            <div v-if="isProcessing" class="mb-3">
                <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">{{ trans('Processing...') }}</span>
                </div>
            </div>

            <div v-else-if="isCompleted" class="mb-3">
                <i class="icofont-check-circled icofont-3x text-success"></i>
            </div>

            <div v-else-if="isFailed" class="mb-3">
                <i class="icofont-close-circled icofont-3x text-danger"></i>
            </div>

            <div v-else-if="isCancelled" class="mb-3">
                <i class="icofont-ban icofont-3x text-warning"></i>
            </div>

            <h5>{{ statusTitle }}</h5>
        </div>

        <div class="progress mb-3" style="height: 25px;">
            <div
                class="progress-bar"
                :class="progressBarClass"
                role="progressbar"
                :style="{ width: progressPercentage + '%' }"
            >
                {{ progressPercentage }}%
            </div>
        </div>

        <div class="row text-center mb-4">
            <div class="col">
                <div class="h4 mb-0">{{ status.processed_rows || 0 }}</div>
                <small class="text-muted">{{ trans('Processed') }}</small>
            </div>
            <div class="col">
                <div class="h4 mb-0 text-success">{{ status.successful_rows || 0 }}</div>
                <small class="text-muted">{{ trans('Successful') }}</small>
            </div>
            <div class="col">
                <div class="h4 mb-0 text-danger">{{ status.failed_rows || 0 }}</div>
                <small class="text-muted">{{ trans('Failed') }}</small>
            </div>
            <div class="col">
                <div class="h4 mb-0">{{ status.total_rows || 0 }}</div>
                <small class="text-muted">{{ trans('Total') }}</small>
            </div>
        </div>

        <div v-if="canBeCancelled" class="text-center mb-4">
            <button
                type="button"
                class="btn btn-outline-danger"
                @click="cancelImport"
                :disabled="cancelling"
            >
                <span v-if="cancelling">{{ trans('Cancelling...') }}</span>
                <span v-else>{{ trans('Cancel import') }}</span>
            </button>
        </div>

        <div v-if="hasErrors" class="mt-4">
            <h6 class="text-danger">
                <i class="icofont-warning-alt"></i>
                {{ trans('Errors') }}
                <span v-if="status.failed_rows > errors.length">
                    ({{ errors.length }} {{ trans('of') }} {{ status.failed_rows }})
                </span>
                <span v-else>({{ errors.length }})</span>
            </h6>
            <div class="table-responsive" style="max-height: 300px; overflow-y: auto;">
                <table class="table table-sm">
                    <thead>
                        <tr>
                            <th>{{ trans('Row') }}</th>
                            <th>{{ trans('Field') }}</th>
                            <th>{{ trans('Error') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(error, index) in errors" :key="index">
                            <td>{{ error.row }}</td>
                            <td>{{ error.field }}</td>
                            <td>{{ error.message }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        importData: {
            type: Object,
            required: true,
        },
        entityType: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            status: { ...this.importData },
            polling: null,
            cancelling: false,
            abortController: null,
        };
    },

    computed: {
        isProcessing() {
            return this.status.status === 'processing';
        },

        isCompleted() {
            return this.status.status === 'completed';
        },

        isFailed() {
            return this.status.status === 'failed';
        },

        isCancelled() {
            return this.status.status === 'cancelled';
        },

        canBeCancelled() {
            return ['pending', 'processing'].includes(this.status.status);
        },

        progressPercentage() {
            return this.status.progress_percentage || 0;
        },

        progressBarClass() {
            if (this.isCompleted) return 'bg-success';
            if (this.isFailed) return 'bg-danger';
            if (this.isCancelled) return 'bg-warning';
            return 'progress-bar-striped progress-bar-animated';
        },

        statusTitle() {
            if (this.isProcessing) return this.trans('Processing import...');
            if (this.isCompleted) return this.trans('Import completed');
            if (this.isFailed) return this.trans('Import failed');
            if (this.isCancelled) return this.trans('Import cancelled');
            return this.trans('Starting import...');
        },

        hasErrors() {
            return this.errors && this.errors.length > 0;
        },

        errors() {
            return this.status.errors || [];
        },

        isFinished() {
            return ['completed', 'failed', 'cancelled'].includes(this.status.status);
        },
    },

    mounted() {
        this.startPolling();
    },

    beforeUnmount() {
        this.abortController?.abort();
        this.stopPolling();
    },

    methods: {
        startPolling() {
            this.fetchStatus();
            this.polling = setInterval(() => {
                if (!this.isFinished) {
                    this.fetchStatus();
                } else {
                    this.stopPolling();
                }
            }, 2000);
        },

        stopPolling() {
            if (this.polling) {
                clearInterval(this.polling);
                this.polling = null;
            }
        },

        async fetchStatus() {
            this.abortController = new AbortController();

            try {
                const response = await axios.get(this.route('imports.status', {
                    entityType: this.entityType,
                    import: this.importData.id,
                }), { signal: this.abortController.signal });

                this.status = response.data;

                if (this.isFinished) {
                    this.stopPolling();
                }
            } catch (error) {
                if (error.name === 'CanceledError') return;
                console.error('Failed to fetch import status:', error);
            }
        },

        async cancelImport() {
            if (this.cancelling) return;

            if (!confirm(this.trans('Are you sure you want to cancel this import?'))) {
                return;
            }

            this.cancelling = true;

            try {
                await axios.post(this.route('imports.cancel', {
                    entityType: this.entityType,
                    import: this.importData.id,
                }));

                this.status.status = 'cancelled';
                this.stopPolling();
            } catch (error) {
                console.error('Failed to cancel import:', error);
            } finally {
                this.cancelling = false;
            }
        },
    },
};
</script>
